import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  formatDateTime,
  getFormattedDateWithoutTmz,
} from "../../../../../../../../utils/date-time-utility";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import Flex from "../../../../../../../../components/Flex";
import StyledDialog from "../../../../../../../../components/StyledDialog";
import Text from "../../../../../../../../components/Text";
import { DisclosureHost } from "../../../../../../../../hooks/use-disclosure";
import { useGetEligibilityByUuid } from "../../../../../../../../queries/eligibility/use-get-eligibility-by-uuid";
import type {
  EligibilityResponseData,
  EligibilityPerson,
  EligibilitySubscriberInfo,
  EligibilityPlan,
  EligibilityBenefit,
  EligibilityCoverage,
  EligibilityDeductibleEntry,
} from "./response";
import {
  GRAY_ACCENT_COLORS,
  getAddressFormat,
} from "../../../../../../../../utils/utils";

type SummaryCardItem = {
  label: string;
  value: string;
  sub: string;
  valueColor?: string;
};

const HEADER_TEAL = "#006064";
const SECTION_TEAL = "#4db6ac";
const ACTIVE_GREEN_BG = "#e8f5e9";
const ACTIVE_GREEN = "#2e7d32";
const DEDUCTIBLE_ORANGE = "#ed6c02";
const LABEL_GREY = "#595F63";
const DASH = "—";

function formatCurrency(amount: string): string {
  const num = Number.parseFloat(amount);
  if (Number.isNaN(num)) return amount;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

/**
 * Sum every numeric amount in a deductible entry list. The payer can return
 * multiple values for a single coverage level (e.g. Family deductible split
 * across $2,000 + $1,500) and the Quick Summary must reflect the total, not
 * just the first value. (ECRH-5111 #5)
 */
function sumAmounts(entries: EligibilityDeductibleEntry[]): number | null {
  const amounts = (entries ?? [])
    .map((e) => Number.parseFloat(e?.amount))
    .filter((n) => !Number.isNaN(n));
  if (amounts.length === 0) return null;
  return amounts.reduce((acc, n) => acc + n, 0);
}

function cardsFromPlans(plans: EligibilityPlan[]): SummaryCardItem[] {
  const cards: SummaryCardItem[] = [];
  for (const plan of plans) {
    if (plan.inNetworkDeductible != null) {
      cards.push({
        label: "In-Network Deductible",
        value: formatCurrency(plan.inNetworkDeductible),
        sub: plan.serviceType,
      });
    }
    if (plan.outNetworkDeductible != null) {
      cards.push({
        label: "Out-of-Network Deductible",
        value: formatCurrency(plan.outNetworkDeductible),
        sub: plan.serviceType,
      });
    }
    if (plan.inNetworkCoInsurance != null) {
      cards.push({
        label: "In-Network Co-Insurance",
        value: formatCoInsurance(plan.inNetworkCoInsurance),
        sub: plan.serviceType,
      });
    }
    if (plan.outNetworkCoInsurance != null) {
      cards.push({
        label: "Out-of-Network Co-Insurance",
        value: formatCoInsurance(plan.outNetworkCoInsurance),
        sub: plan.serviceType,
      });
    }
  }
  return cards;
}

function cardsFromBenefits(benefits: EligibilityBenefit[]): SummaryCardItem[] {
  const cards: SummaryCardItem[] = [];
  for (const benefit of benefits) {
    for (const coverage of benefit.coverages) {
      const deductibleRemaining = sumAmounts(coverage.deductibleRemaining);
      if (deductibleRemaining != null) {
        cards.push({
          label: "Deductible Remaining",
          value: formatCurrency(String(deductibleRemaining)),
          sub: `${benefit.serviceType} — ${coverage.coverageLevel} — As of verification date`,
          valueColor: DEDUCTIBLE_ORANGE,
        });
      }
      const deductible = sumAmounts(coverage.deductible);
      if (deductible != null) {
        cards.push({
          label: "Deductible",
          value: formatCurrency(String(deductible)),
          sub: `${benefit.serviceType} — ${coverage.coverageLevel}`,
        });
      }
      if (coverage.coPays?.length > 0 && coverage.coPays[0] != null) {
        cards.push({
          label: "Co-Pay",
          value: `$${coverage.coPays[0]}`,
          sub: `${benefit.serviceType} — ${coverage.coverageLevel}`,
          valueColor:
            Number.parseFloat(coverage.coPays[0]) === 0
              ? ACTIVE_GREEN
              : undefined,
        });
      }
    }
  }
  return cards;
}

function buildQuickReferenceSummary(
  data: EligibilityResponseData
): SummaryCardItem[] {
  return [...cardsFromPlans(data.plans), ...cardsFromBenefits(data.benefits)];
}

function formatDisplayDate(dateStr: string): string {
  if (!dateStr) return "";
  return getFormattedDateWithoutTmz(dateStr) ?? dateStr;
}

function formatGeneratedDate(iso: string): string {
  return formatDateTime(iso) ?? iso;
}

function formatCoInsurance(value: string | null): string {
  if (value == null || value === "") return DASH;
  const num = Number.parseFloat(value);
  if (Number.isNaN(num)) return value;
  return `${Math.round(num * 100)}%`;
}

function truncateUuid(uuid: string): string {
  if (!uuid) return "";
  return uuid.length > 12 ? `${uuid.slice(0, 8)}-${uuid.slice(9, 13)}` : uuid;
}

function personDisplayName(person: EligibilityPerson): string {
  return [person.firstName, person.lastName].filter(Boolean).join(" ") || DASH;
}

function personAddress(person: EligibilityPerson): string {
  if (typeof person?.address === "object") {
    return getAddressFormat(person?.address ?? undefined);
  }

  return person.address ?? "Not on File";
}

function personGender(person: EligibilityPerson): string {
  return person.gender ?? "Not Specified";
}

function subscriberInfoName(info: EligibilitySubscriberInfo): string {
  return info.fullName?.trim() || DASH;
}

function subscriberInfoGender(info: EligibilitySubscriberInfo): string {
  return info.gender ?? "Not Specified";
}

function subscriberInfoAddress(info: EligibilitySubscriberInfo): string {
  if (!info.address) return "Not on File";
  return (
    getAddressFormat({
      line1: info.address.line1 ?? undefined,
      line2: info.address.line2 ?? undefined,
      city: info.address.city ?? undefined,
      state: info.address.state ?? undefined,
      country: info.address.country ?? undefined,
      zipcode: info.address.zipcode ?? undefined,
    }) || "Not on File"
  );
}

interface EligibilityReportModalProps {
  disclosureHost: DisclosureHost;
}

const EligibilityResponseModal = ({
  disclosureHost,
}: EligibilityReportModalProps) => {
  const rowData = disclosureHost.data as
    Partial<EligibilityResponseData> | undefined;
  const eligibilityUuid = rowData?.uuid;

  const { data: fetchedData, isLoading } = useGetEligibilityByUuid({
    eligibilityUuid,
  });

  // The endpoint returns a standard ApiResponse wrapper
  // ({ code, message, data, ... }); the eligibility payload lives in `.data`.
  // Fall back to `fetchedData` itself in case it is already unwrapped.
  const data = ((fetchedData as unknown as { data?: EligibilityResponseData })
    ?.data ?? fetchedData) as EligibilityResponseData | undefined;

  // These fields come through on the list row as well; prefer the detailed
  // payload but fall back to that row.
  const insuranceId = data?.insuranceId ?? rowData?.insuranceId;
  const groupNumber = data?.groupNumber ?? rowData?.groupNumber;
  const planNumber = data?.planNumber ?? rowData?.planNumber;
  const transactionId = data?.transactionId ?? rowData?.transactionId;
  const effectiveStartDate =
    data?.effectiveStartDate ?? rowData?.effectiveStartDate;
  const effectiveEndDate = data?.effectiveEndDate ?? rowData?.effectiveEndDate;

  const closeModal = () => {
    disclosureHost.close();
  };

  if (isLoading && !data) {
    return (
      <StyledDialog
        open={disclosureHost.opened}
        onClose={closeModal}
        dialogTitle="Eligibility Response"
        dialogAction={
          <Button variant="outlined" onClick={closeModal}>
            Close
          </Button>
        }
      >
        <Flex justifyContent="center" alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Flex>
      </StyledDialog>
    );
  }

  if (!data) {
    return (
      <StyledDialog
        open={disclosureHost.opened}
        onClose={closeModal}
        dialogTitle="Eligibility Response"
        dialogAction={
          <Button variant="outlined" onClick={closeModal}>
            Close
          </Button>
        }
      >
        <Text>No eligibility data available.</Text>
      </StyledDialog>
    );
  }

  const planDisplayName = (plan: EligibilityPlan, index: number): string => {
    const name = plan.name || plan.serviceType;
    return `PLAN ${index + 1} — ${name}`;
  };

  return (
    <StyledDialog
      open={disclosureHost.opened}
      onClose={closeModal}
      dialogTitle="Eligibility Response"
      dialogAction={
        <Flex gap={2}>
          <Button variant="outlined" onClick={closeModal}>
            Close
          </Button>
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "900px",
          width: "100%",
        },
      }}
    >
      <Box sx={{ pb: 1 }}>
        {/* Main Header */}
        <Box
          sx={{
            bgcolor: HEADER_TEAL,
            color: "white",
            px: 2,
            py: 2,
            borderRadius: "4px 4px 0 0",
          }}
        >
          <Flex
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Text
                fontWeight={700}
                sx={{
                  color: "white",
                  fontSize: "1.25rem",
                  textTransform: "uppercase",
                }}
              >
                INSURANCE ELIGIBILITY VERIFICATION
              </Text>
              <Text sx={{ color: "white", fontSize: "0.875rem", mt: 0.5 }}>
                Real-Time Benefit Verification Report
              </Text>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Text sx={{ color: "white", fontSize: "0.75rem" }}>
                Verification ID
              </Text>
              <Text
                sx={{ color: "white", fontSize: "0.875rem", fontWeight: 500 }}
              >
                {truncateUuid(data.uuid)}
              </Text>
              <Text sx={{ color: "white", fontSize: "0.75rem", mt: 0.5 }}>
                Generated: {formatGeneratedDate(data.created)}
              </Text>
              {transactionId ? (
                <Text sx={{ color: "white", fontSize: "0.75rem", mt: 0.5 }}>
                  Transaction ID: {transactionId}
                </Text>
              ) : null}
            </Box>
          </Flex>
        </Box>

        {/* Summary */}
        <Box
          sx={{
            bgcolor: "white",
            px: 2,
            py: 2,
            border: "1px solid #e0e0e0",
            borderTop: 0,
          }}
        >
          <Flex gap={3} flexWrap="wrap" alignItems="center">
            <Box
              sx={{
                bgcolor: ACTIVE_GREEN_BG,
                px: 2,
                py: 1.5,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <FiberManualRecordIcon
                sx={{ color: ACTIVE_GREEN, fontSize: 16 }}
              />
              <Text fontWeight={700} sx={{ color: ACTIVE_GREEN }}>
                {data.status}
              </Text>
              <Text sx={{ color: LABEL_GREY, fontSize: "0.75rem" }}>
                Coverage Status
              </Text>
            </Box>
            <Box>
              <Text sx={{ color: LABEL_GREY, fontSize: "0.75rem" }}>
                Requested By
              </Text>
              <Text fontWeight={600}>{data.requestedBy}</Text>
            </Box>
            <Box>
              <Text sx={{ color: LABEL_GREY, fontSize: "0.75rem" }}>Payer</Text>
              <Text fontWeight={600}>{data.payer.name}</Text>
              <Text sx={{ color: LABEL_GREY, fontSize: "0.75rem" }}>
                Code: {data.payer.code}
              </Text>
            </Box>
            <Box>
              <Text sx={{ color: LABEL_GREY, fontSize: "0.75rem" }}>
                Relationship
              </Text>
              <Text fontWeight={600}>{data.subscriberRelationship}</Text>
            </Box>
            {insuranceId ? (
              <Box>
                <Text sx={{ color: LABEL_GREY, fontSize: "0.75rem" }}>
                  Insurance ID
                </Text>
                <Text fontWeight={600}>{insuranceId}</Text>
              </Box>
            ) : null}
            {groupNumber ? (
              <Box>
                <Text sx={{ color: LABEL_GREY, fontSize: "0.75rem" }}>
                  Group Number
                </Text>
                <Text fontWeight={600}>{groupNumber}</Text>
              </Box>
            ) : null}
            {planNumber ? (
              <Box>
                <Text sx={{ color: LABEL_GREY, fontSize: "0.75rem" }}>
                  Plan Number
                </Text>
                <Text fontWeight={600}>{planNumber}</Text>
              </Box>
            ) : null}

            {effectiveStartDate || effectiveEndDate ? (
              <Box>
                <Text sx={{ color: LABEL_GREY, fontSize: "0.75rem" }}>
                  Coverage Effective Period
                </Text>
                <Text fontWeight={600}>
                  {`${
                    effectiveStartDate
                      ? formatDisplayDate(effectiveStartDate)
                      : DASH
                  } - ${
                    effectiveEndDate
                      ? formatDisplayDate(effectiveEndDate)
                      : DASH
                  }`}
                </Text>
              </Box>
            ) : null}
          </Flex>
        </Box>

        {/* Patient & Subscriber */}
        <Box sx={{ border: "1px solid #e0e0e0", borderTop: 0 }}>
          <Box
            sx={{
              bgcolor: GRAY_ACCENT_COLORS.backgroundColor,
              px: 2,
              py: 1,
              borderBottom: "1px solid black",
            }}
          >
            <Text
              fontWeight={700}
              sx={{ fontSize: "0.875rem", textTransform: "uppercase" }}
            >
              PATIENT & SUBSCRIBER INFORMATION
            </Text>
          </Box>
          <Flex sx={{ width: "100%" }}>
            <Box sx={{ flex: 1, borderRight: "1px solid #e0e0e0" }}>
              <Box
                sx={{
                  bgcolor: GRAY_ACCENT_COLORS.backgroundColor,
                  py: 1,
                  textAlign: "center",
                }}
              >
                <Text fontWeight={700} sx={{ fontSize: "0.75rem" }}>
                  PATIENT
                </Text>
              </Box>
              <Box sx={{ px: 2, py: 1.5 }}>
                <DetailRow
                  label="Full Name"
                  // value={personDisplayName(data.patient)}
                  value={data?.subscriber?.fullName}
                />
                <DetailRow
                  label="Date of Birth"
                  value={formatDisplayDate(data.patient.birthDate)}
                />
                <DetailRow label="Gender" value={personGender(data.patient)} />
                <DetailRow
                  label="Address"
                  value={personAddress(data.patient)}
                />
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  bgcolor: GRAY_ACCENT_COLORS.backgroundColor,
                  py: 1,
                  textAlign: "center",
                }}
              >
                <Text fontWeight={700} sx={{ fontSize: "0.75rem" }}>
                  SUBSCRIBER
                </Text>
              </Box>
              <Box sx={{ px: 2, py: 1.5 }}>
                {data.subscriberInfo ? (
                  <>
                    <DetailRow
                      label="Full Name"
                      value={subscriberInfoName(data.subscriberInfo)}
                    />
                    <DetailRow
                      label="Date of Birth"
                      value={formatDisplayDate(data.subscriberInfo.dob ?? "")}
                    />
                    <DetailRow
                      label="Gender"
                      value={subscriberInfoGender(data.subscriberInfo)}
                    />
                    <DetailRow
                      label="Address"
                      value={subscriberInfoAddress(data.subscriberInfo)}
                    />
                  </>
                ) : (
                  <>
                    <DetailRow
                      label="Full Name"
                      value={personDisplayName(data.subscriber)}
                    />
                    <DetailRow
                      label="Date of Birth"
                      value={formatDisplayDate(data.subscriber.birthDate)}
                    />
                    <DetailRow
                      label="Gender"
                      value={personGender(data.subscriber)}
                    />
                    <DetailRow
                      label="Address"
                      value={personAddress(data.subscriber)}
                    />
                  </>
                )}
              </Box>
            </Box>
          </Flex>
        </Box>

        {/* Active Insurance Plans */}
        <Box sx={{ border: "1px solid #e0e0e0", borderTop: 0, mt: 0 }}>
          <Box
            sx={{
              bgcolor: GRAY_ACCENT_COLORS.backgroundColor,
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Text
              fontWeight={700}
              sx={{ fontSize: "0.875rem", textTransform: "uppercase" }}
            >
              ACTIVE INSURANCE PLANS
            </Text>
          </Box>
          {data.plans.map((plan, index) => (
            <Box
              key={`${plan.serviceType}-${index}`}
              sx={{ borderBottom: "1px solid #e0e0e0" }}
            >
              <Flex
                sx={{
                  px: 2,
                  py: 1,
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text fontWeight={700} sx={{ fontSize: "0.8rem" }}>
                  {planDisplayName(plan, index)}
                </Text>
                {plan.active && (
                  <Flex alignItems="center" gap={0.5}>
                    <FiberManualRecordIcon
                      sx={{ color: ACTIVE_GREEN, fontSize: 14 }}
                    />
                    <Text
                      fontWeight={600}
                      sx={{ color: ACTIVE_GREEN, fontSize: "0.8rem" }}
                    >
                      Active
                    </Text>
                  </Flex>
                )}
              </Flex>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        Insurance Type
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        Service Type
                      </TableCell>
                      {plan.inNetworkDeductible != null ||
                      plan.outNetworkDeductible != null ? (
                        <>
                          <TableCell sx={{ fontWeight: 500 }}>
                            In-Network Deductible
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            Out-of-Network Deductible
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell sx={{ fontWeight: 500 }}>
                            In-Network Co-Insurance
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            Out-of-Network Co-Insurance
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {plan.insuranceType ?? DASH}
                      </TableCell>
                      <TableCell>{plan.serviceType}</TableCell>
                      {plan.inNetworkDeductible != null ||
                      plan.outNetworkDeductible != null ? (
                        <>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {plan.inNetworkDeductible == null
                              ? DASH
                              : formatCurrency(plan.inNetworkDeductible)}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {plan.outNetworkDeductible == null
                              ? DASH
                              : formatCurrency(plan.outNetworkDeductible)}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {formatCoInsurance(plan.inNetworkCoInsurance)}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {formatCoInsurance(plan.outNetworkCoInsurance)}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
        </Box>

        {/* Benefits Detail */}
        <Box sx={{ border: "1px solid #e0e0e0", borderTop: 0 }}>
          <Box
            sx={{
              bgcolor: "white",
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Text
              fontWeight={700}
              sx={{ fontSize: "0.875rem", textTransform: "uppercase" }}
            >
              BENEFITS DETAIL
            </Text>
          </Box>
          {data.benefits.map((benefit, bIndex) => (
            <BenefitsDetailSection
              key={`${benefit.serviceType}-${bIndex}`}
              benefit={benefit}
            />
          ))}
        </Box>

        {/* Quick Reference Summary */}
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderTop: 0,
            px: 2,
            py: 2,
            bgcolor: "#f5f5f5",
            borderRadius: "0 0 4px 4px",
          }}
        >
          <Text
            fontWeight={700}
            sx={{ fontSize: "0.875rem", textTransform: "uppercase", mb: 1.5 }}
          >
            QUICK REFERENCE SUMMARY
          </Text>
          <Flex gap={2} flexWrap="wrap">
            {buildQuickReferenceSummary(data).map((card, idx) => (
              <SummaryCard
                key={`${card.label}-${card.sub}-${idx}`}
                label={card.label}
                value={card.value}
                sub={card.sub}
                valueColor={card.valueColor}
              />
            ))}
          </Flex>
        </Box>
      </Box>
    </StyledDialog>
  );
};

function DetailRow({
  label,
  value,
}: Readonly<{ label: string; value: string }>) {
  return (
    <Flex justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
      <Text sx={{ color: LABEL_GREY, fontSize: "0.8125rem" }}>{label}</Text>
      <Text fontWeight={600} sx={{ fontSize: "0.8125rem" }}>
        {value}
      </Text>
    </Flex>
  );
}

function BenefitsDetailSection({
  benefit,
}: Readonly<{ benefit: EligibilityBenefit }>) {
  const hasCoveragePct = benefit.coverages.some(
    (c) => c.coveragePercentages?.length > 0
  );
  return (
    <Box sx={{ borderBottom: "1px solid #e0e0e0" }}>
      <Text
        fontWeight={700}
        sx={{ fontSize: "0.9375rem", px: 2, pt: 1.5, pb: 0.5 }}
      >
        {benefit.serviceType}
      </Text>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: GRAY_ACCENT_COLORS.backgroundColor }}>
              <TableCell sx={{ fontWeight: 600 }}>Coverage Level</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              {hasCoveragePct ? (
                <TableCell sx={{ fontWeight: 600 }}>Coverage %</TableCell>
              ) : (
                <TableCell sx={{ fontWeight: 600 }}>Insurance Type</TableCell>
              )}
              <TableCell sx={{ fontWeight: 600 }}>Deductible</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                Deductible Remaining
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Co-Pay</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {benefit.coverages.map((cov, cIndex) => (
              <BenefitsDetailRow
                key={`${cov.coverageLevel}-${cIndex}`}
                coverage={cov}
                hasCoveragePct={hasCoveragePct}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function BenefitsDetailRow({
  coverage,
  hasCoveragePct,
}: Readonly<{ coverage: EligibilityCoverage; hasCoveragePct: boolean }>) {
  const deductibleStrs =
    coverage.deductible?.length > 0
      ? coverage.deductible.map((d) => formatCurrency(d.amount))
      : [];
  const deductibleRemainingStrs =
    coverage.deductibleRemaining?.length > 0
      ? coverage.deductibleRemaining.map((d) => formatCurrency(d.amount))
      : [];
  const coPayDisplay =
    coverage.coPays?.length > 0 ? `$${coverage.coPays[0]}` : DASH;
  const coveragePctDisplay =
    coverage.coveragePercentages?.length > 0
      ? coverage.coveragePercentages.join(", ")
      : DASH;

  return (
    <TableRow>
      <TableCell>{coverage.coverageLevel}</TableCell>
      <TableCell>
        {coverage.coverageStatus ? (
          <Flex alignItems="center" gap={0.5}>
            <FiberManualRecordIcon sx={{ color: ACTIVE_GREEN, fontSize: 14 }} />
            <Text sx={{ color: ACTIVE_GREEN, fontWeight: 600 }}>
              {coverage.coverageStatus}
            </Text>
          </Flex>
        ) : (
          DASH
        )}
      </TableCell>
      {hasCoveragePct ? (
        <TableCell>{coveragePctDisplay}</TableCell>
      ) : (
        <TableCell>{coverage.insuranceType ?? DASH}</TableCell>
      )}
      <TableCell>
        {deductibleStrs.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            {deductibleStrs.map((s, i) => (
              <Text key={`ded-${i}-${s}`}>{s}</Text>
            ))}
          </Box>
        ) : (
          DASH
        )}
      </TableCell>
      <TableCell>
        {deductibleRemainingStrs.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            {deductibleRemainingStrs.map((s, i) => (
              <Text
                key={`rem-${i}-${s}`}
                fontWeight={600}
                sx={{ color: DEDUCTIBLE_ORANGE }}
              >
                {s}
              </Text>
            ))}
          </Box>
        ) : (
          DASH
        )}
      </TableCell>
      <TableCell>
        {coverage.coPays?.length > 0 ? (
          <Text
            fontWeight={600}
            sx={{
              color:
                Number.parseFloat(coverage.coPays[0]) === 0
                  ? ACTIVE_GREEN
                  : undefined,
            }}
          >
            {coPayDisplay}
          </Text>
        ) : (
          DASH
        )}
      </TableCell>
    </TableRow>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  valueColor,
}: Readonly<{
  label: string;
  value: string;
  sub: string;
  valueColor?: string;
}>) {
  return (
    <Box
      sx={{
        bgcolor: "white",
        flex: "1 1 180px",
        minWidth: 180,
        maxWidth: 240,
        p: 1.5,
        borderRadius: 1,
        borderTop: "3px solid",
        borderColor: SECTION_TEAL,
      }}
    >
      <Text sx={{ color: LABEL_GREY, fontSize: "0.75rem" }}>{label}</Text>
      <Text
        fontWeight={700}
        sx={{ fontSize: "1.125rem", color: valueColor ?? "text.primary" }}
      >
        {value}
      </Text>
      <Text sx={{ color: LABEL_GREY, fontSize: "0.7rem", mt: 0.5 }}>{sub}</Text>
    </Box>
  );
}

export default EligibilityResponseModal;
